import { useTheme } from '@mui/material/styles';
import { useMemo, type ReactNode } from 'react';

interface SpeedGaugeProps {
  value: number; // Current speed value in Mbps
  maxValue?: number; // Maximum speed value
  size?: number; // Size of the gauge (width & height will be set accordingly)
  label?: 'download' | 'upload'; // Optional label below value: 'download' | 'upload'
}

// Non-linear scale ticks matching Speedtest style
const SCALE_TICKS = [0, 5, 10, 50, 100, 250, 500, 750, 1000];

// Arc configuration (rotated 90° clockwise)
const ARC_START_ANGLE = 225;
const ARC_END_ANGLE = 495;
const ARC_SWEEP = ARC_END_ANGLE - ARC_START_ANGLE; // 270 degrees

/**
 * Converts a speed value to an angle on the gauge using a logarithmic-like
 * non-linear mapping that matches the Speedtest scale distribution.
 */
function valueToAngle(value: number): number {
  const clampedValue = Math.max(0, Math.min(value, 1000));

  // Find which segment the value falls in
  for (let i = 0; i < SCALE_TICKS.length - 1; i++) {
    const low = SCALE_TICKS[i];
    const high = SCALE_TICKS[i + 1];
    if (clampedValue >= low && clampedValue <= high) {
      const segmentFraction = (clampedValue - low) / (high - low);
      const anglePerSegment = ARC_SWEEP / (SCALE_TICKS.length - 1);
      const angle =
        ARC_START_ANGLE +
        i * anglePerSegment +
        segmentFraction * anglePerSegment;
      return angle;
    }
  }
  return ARC_END_ANGLE;
}

/**
 * Converts degrees to radians
 */
function degToRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

/**
 * Gets x,y coordinates on a circle given center, radius, and angle in degrees
 */
function polarToCartesian(
  cx: number,
  cy: number,
  radius: number,
  angleDeg: number
) {
  const rad = degToRad(angleDeg - 90);
  return {
    x: cx + radius * Math.cos(rad),
    y: cy + radius * Math.sin(rad),
  };
}

/**
 * Creates an SVG arc path
 */
function describeArc(
  cx: number,
  cy: number,
  radius: number,
  startAngle: number,
  endAngle: number
): string {
  const start = polarToCartesian(cx, cy, radius, endAngle);
  const end = polarToCartesian(cx, cy, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
  return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
}

function Gauge({ value, size = 280, label }: SpeedGaugeProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const cx = size / 2;
  const cy = size / 2;
  const outerRadius = size * 0.42;
  const arcWidth = size * 0.12;
  const arcRadius = outerRadius - arcWidth / 2;

  const needleAngle = useMemo(() => valueToAngle(value), [value]);

  const gradientId =
    label === 'upload'
      ? 'speedGaugeGradientUpload'
      : 'speedGaugeGradientDownload';
  const glowId =
    label === 'upload' ? 'speedGaugeGlowUpload' : 'speedGaugeGlowDownload';
  const needleGradientId =
    label === 'upload' ? 'needleGradientUpload' : 'needleGradientDownload';

  const arcStops =
    label === 'upload'
      ? [
          { offset: '0%', color: theme.palette.secondary['04'] },
          { offset: '50%', color: theme.palette.secondary['02'] },
          { offset: '100%', color: theme.palette.secondary.main },
        ]
      : [
          {
            offset: '0%',
            color: theme.palette.primary['04'] ?? theme.palette.primary.dark,
          },
          { offset: '50%', color: theme.palette.primary['02'] },
          { offset: '100%', color: theme.palette.primary.main },
        ];

  // Calculate the arc path for the full background
  const fullArcPath = describeArc(
    cx,
    cy,
    arcRadius,
    ARC_START_ANGLE,
    ARC_END_ANGLE
  );

  // Calculate the arc path for the value (filled portion)
  const valueEndAngle = Math.min(needleAngle, ARC_END_ANGLE);
  const valueArcPath =
    value > 0
      ? describeArc(cx, cy, arcRadius, ARC_START_ANGLE, valueEndAngle)
      : '';

  const needleTip = polarToCartesian(
    cx,
    cy,
    arcRadius + arcWidth * 0.3,
    needleAngle
  );
  const needleBase1 = polarToCartesian(cx, cy, 6, needleAngle + 90);
  const needleBase2 = polarToCartesian(cx, cy, 6, needleAngle - 90);

  const arcStart = polarToCartesian(cx, cy, arcRadius, ARC_START_ANGLE);
  const arcEnd = polarToCartesian(cx, cy, arcRadius, ARC_END_ANGLE);

  // Tick marks
  const tickElements = SCALE_TICKS.map((tick, index) => {
    const tickAngle =
      ARC_START_ANGLE + (index / (SCALE_TICKS.length - 1)) * ARC_SWEEP;
    const labelPoint = polarToCartesian(
      cx,
      cy,
      arcRadius + arcWidth * 2.2,
      tickAngle
    );

    return (
      <g key={tick}>
        <text
          x={labelPoint.x}
          y={labelPoint.y}
          textAnchor="middle"
          dominantBaseline="central"
          fill={theme.palette.grey['01']}
          fontSize={size * 0.04}
          fontFamily="Poppins, sans-serif"
          fontWeight={500}
        >
          {tick}
        </text>
      </g>
    );
  });

  // Small dot marks between main ticks
  const dotElements: ReactNode[] = [];
  for (let i = 0; i < SCALE_TICKS.length - 1; i++) {
    const startTickAngle =
      ARC_START_ANGLE + (i / (SCALE_TICKS.length - 1)) * ARC_SWEEP;
    const endTickAngle =
      ARC_START_ANGLE + ((i + 1) / (SCALE_TICKS.length - 1)) * ARC_SWEEP;
    // 3 subdivision dots between each major tick
    for (let j = 1; j <= 3; j++) {
      const subAngle =
        startTickAngle + (j / 4) * (endTickAngle - startTickAngle);
      const dotPoint = polarToCartesian(
        cx,
        cy,
        arcRadius + arcWidth * 0.8,
        subAngle
      );
      dotElements.push(
        <circle
          key={`dot-${i}-${j}`}
          cx={dotPoint.x}
          cy={dotPoint.y}
          r={1.2}
          fill={theme.palette.grey['02']}
        />
      );
    }
  }

  const displayValue = value.toFixed(2);

  return (
    <svg
      width={size}
      height={size + size * 0.22}
      viewBox={`0 0 ${size} ${size + size * 0.22}`}
      style={{ overflow: 'visible' }}
    >
      <defs>
        {/* Linear gradient that follows the arc direction (start → end) */}
        <linearGradient
          id={gradientId}
          gradientUnits="userSpaceOnUse"
          x1={arcStart.x}
          y1={arcStart.y}
          x2={arcEnd.x}
          y2={arcEnd.y}
        >
          {arcStops.map((stop) => (
            <stop
              key={stop.offset}
              offset={stop.offset}
              stopColor={stop.color}
            />
          ))}
        </linearGradient>

        {/* Glow filter */}
        <filter id={glowId} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Needle gradient */}
        <linearGradient id={needleGradientId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop
            offset="0%"
            stopColor={
              isDark ? theme.palette.text.primary : theme.palette.grey['01']
            }
          />
          <stop offset="100%" stopColor={theme.palette.grey['01']} />
        </linearGradient>
      </defs>

      {/* Dot subdivisions */}
      {dotElements}

      {/* Background arc (unfilled portion - darker) */}
      <path
        d={fullArcPath}
        fill="none"
        stroke={isDark ? theme.palette.grey['03'] : theme.palette.grey['02']}
        strokeWidth={arcWidth}
        strokeLinecap="round"
      />

      {/* Value arc (filled portion with gradient) */}
      {value > 0 && (
        <path
          d={valueArcPath}
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth={arcWidth}
          strokeLinecap="round"
          filter={`url(#${glowId})`}
        />
      )}

      {/* Tick labels */}
      {tickElements}

      {/* Needle */}
      <path
        d={`M ${needleBase1.x} ${needleBase1.y} L ${needleTip.x} ${needleTip.y} L ${needleBase2.x} ${needleBase2.y} Z`}
        fill={`url(#${needleGradientId})`}
        style={{
          filter: isDark
            ? 'drop-shadow(0 0 4px rgba(0,0,0,0.5))'
            : 'drop-shadow(0 0 2px rgba(0,0,0,0.2))',
        }}
      />

      {/* Needle center dot */}
      <circle
        cx={cx}
        cy={cy}
        r={7}
        fill={theme.palette.grey['02']}
        stroke={isDark ? theme.palette.grey['01'] : theme.palette.grey['02']}
        strokeWidth={2}
      />

      {/* Value display — bottom of gauge */}
      <text
        x={cx}
        y={size + size * 0.06}
        textAnchor="middle"
        dominantBaseline="central"
        fill={theme.palette.text.primary}
        fontSize={size * 0.14}
        fontFamily="Poppins, sans-serif"
        fontWeight={700}
        letterSpacing="-0.5px"
      >
        {displayValue}
      </text>

      {/* Mbps label — below value */}
      <text
        x={cx}
        y={size + size * 0.17}
        textAnchor="middle"
        dominantBaseline="central"
        fill={theme.palette.primary.main}
        fontSize={size * 0.05}
        fontFamily="Poppins, sans-serif"
        fontWeight={500}
      >
        {label === 'download'
          ? '\u2b07 Mbps'
          : label === 'upload'
            ? '\u2b06 Mbps'
            : 'Mbps'}
      </text>
    </svg>
  );
}

export default Gauge;
